/**
 * Mobile input manager that selects between gyroscope tilt controls
 * and touch D-pad fallback based on device capabilities.
 *
 * Priority:
 * 1. Gyroscope/accelerometer tilt (if available and receiving events)
 * 2. Touch D-pad fallback
 */
import Phaser from 'phaser';
import { TiltInput } from '@/input/tilt-input';
import { TouchInput } from '@/input/touch-input';

type Direction = 'left' | 'right' | 'up' | 'down' | 'none';

/** Delay before deciding gyroscope isn't actually working (ms). */
const GYRO_DETECTION_TIMEOUT_MS = 1500;

export class MobileInput {
  private readonly tiltInput: TiltInput;
  private touchInput: TouchInput | null = null;
  private readonly scene: Phaser.Scene;
  private useTilt = false;
  private initialized = false;
  private detectionTimer = 0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.tiltInput = new TiltInput();

    if (this.tiltInput.getIsAvailable()) {
      // Start listening for tilt events - will fall back to touch if no events arrive
      this.tiltInput.start();
      this.tiltInput.calibrate();
    } else {
      // No gyroscope at all - immediately create touch controls
      this.createTouchFallback();
      this.initialized = true;
    }
  }

  /** Returns true if device supports mobile input (touch or tilt). */
  static isMobileDevice(): boolean {
    // Allow forcing mobile mode via query parameter for testing
    const params = new URLSearchParams(window.location.search);
    if (params.get('mobile') === '1') {
      return true;
    }
    return ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
  }

  getDirection(): Direction {
    if (this.useTilt) {
      return this.tiltInput.getDirection();
    }
    if (this.touchInput) {
      return this.touchInput.getDirection();
    }
    return 'none';
  }

  /** Call from scene update to handle detection timeout. */
  update(delta: number): void {
    if (this.initialized) {
      return;
    }

    this.detectionTimer += delta;

    // Check if we received a real gyroscope event
    if (this.tiltInput.getHasReceivedEvent()) {
      this.useTilt = true;
      this.initialized = true;
      return;
    }

    // If timeout elapsed without gyroscope events, fall back to touch
    if (this.detectionTimer >= GYRO_DETECTION_TIMEOUT_MS) {
      this.tiltInput.stop();
      this.createTouchFallback();
      this.initialized = true;
    }
  }

  /** Returns true if using tilt controls, false if using touch. */
  isUsingTilt(): boolean {
    return this.useTilt;
  }

  /** Returns true if touch controls are active (for feature #88 verification). */
  hasTouchControls(): boolean {
    return this.touchInput !== null;
  }

  /** Recalibrate tilt if applicable. */
  calibrate(): void {
    if (this.useTilt) {
      this.tiltInput.calibrate();
    }
  }

  destroy(): void {
    this.tiltInput.destroy();
    if (this.touchInput) {
      this.touchInput.destroy();
      this.touchInput = null;
    }
  }

  private createTouchFallback(): void {
    this.touchInput = new TouchInput(this.scene);
    this.useTilt = false;
  }
}
